import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import { numberFormat } from '../helpers/GlobalHelper';
import showLoader from '../helpers/ShowLoader';

type ProjectOption = { value: string; label: string };

type BalanceSheetNode = {
    name: string;
    amount: number | null;
    level: number;
    children: BalanceSheetNode[];
    isLeaf: boolean;
    ledgerId?: number | null;
    isNegative?: boolean;
};

type BalanceSheetSection = {
    title: string;
    total: number;
    items: BalanceSheetNode[];
};

type BalanceSheetHeader = {
    projectName?: string | null;
    fromDate: string;
    toDate: string;
};

type BalanceSheetPayload = {
    header: BalanceSheetHeader;
    assets: BalanceSheetSection;
    liabilities: BalanceSheetSection;
};

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const today = new Date();
const fiscalYearStart =
    today.getMonth() < 6 ? new Date(today.getFullYear() - 1, 6, 1) : new Date(today.getFullYear(), 6, 1);
const fiscalYearEnd = new Date(fiscalYearStart.getFullYear() + 1, 5, 30);

export default function BalanceSheetV2() {
    const [reportData, setReportData] = useState<BalanceSheetPayload | null>(null);
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
    const [startDate, setStartDate] = useState<string>(formatDate(fiscalYearStart));
    const [endDate, setEndDate] = useState<string>(formatDate(fiscalYearEnd));
    const [searchAssets, setSearchAssets] = useState('');
    const [searchLiabilities, setSearchLiabilities] = useState('');
    const [loading, setLoading] = useState(false);

    const formatAmount = useCallback((value?: number | null) => {
        if (value === null || value === undefined) {
            return '-';
        }

        if (value < 0) {
            return `(${numberFormat(Math.abs(value))})`;
        }

        return numberFormat(value);
    }, []);

    const filterNodesByQuery = useCallback((nodes: BalanceSheetNode[], query: string): BalanceSheetNode[] => {
        if (!query) {
            return nodes;
        }
        const lowered = query.toLowerCase();

        return nodes
            .map((node) => {
                const filteredChildren = filterNodesByQuery(node.children || [], query);
                if (node.name.toLowerCase().includes(lowered) || filteredChildren.length > 0) {
                    return { ...node, children: filteredChildren };
                }
                return null;
            })
            .filter(Boolean) as BalanceSheetNode[];
    }, []);

    const renderNodes = useCallback(
        (nodes: BalanceSheetNode[], parentKey: string): React.ReactElement[] => {
            return nodes.flatMap((node, index) => {
                const key = `${parentKey}-${node.name}-${index}`;
                const childRows = renderNodes(node.children || [], key);
                const baseAmount = node.amount ?? 0;
                const displayAmount = node.isNegative ? -Math.abs(baseAmount) : baseAmount;
                const isNegativeRow =
                    node.isNegative || (typeof displayAmount === 'number' && displayAmount < 0);
                const hasLedgerLink = node.isLeaf && !!node.ledgerId && !!selectedProject?.value && startDate && endDate;
                const ledgerUrl = hasLedgerLink
                    ? `/reports?type=ledgerStatement&idProjects=${selectedProject?.value ?? ''}&idAccLedgers=${
                          node.ledgerId
                      }&fromDate=${startDate}&toDate=${endDate}`
                    : null;

                return [
                    <tr key={key} className={isNegativeRow ? 'table-danger' : ''}>
                        <td
                            className={!node.isLeaf ? 'fw-semibold' : ''}
                            style={{ paddingLeft: `${Math.max(0, node.level) * 18}px` }}
                        >
                            {hasLedgerLink && ledgerUrl ? (
                                <>
                                {/* @ts-ignore */}
                                <Link to={ledgerUrl} target="_blank" rel="noopener noreferrer">
                                    {node.name}
                                </Link>
                                </>
                            ) : (
                                node.name
                            )}
                        </td>
                        <td className="text-end">{formatAmount(displayAmount)}</td>
                    </tr>,
                    ...childRows,
                ];
            }) as React.ReactElement[];
        },
        [endDate, formatAmount, selectedProject?.value, startDate]
    );

    const reorderNetSummaryNodes = useCallback((nodes: BalanceSheetNode[]): BalanceSheetNode[] => {
        const enriched = nodes.map((node) => ({
            ...node,
            children: reorderNetSummaryNodes(node.children || []),
        }));

        const isNetSummary = (node: BalanceSheetNode) => {
            const normalized = node.name.trim().toLowerCase();
            return normalized === 'net profit' || normalized === 'net loss';
        };

        const netNodes = enriched.filter(isNetSummary);
        const otherNodes = enriched.filter((node) => !isNetSummary(node));

        return [...otherNodes, ...netNodes];
    }, []);

    const renderSection = (
        section: BalanceSheetSection | undefined,
        query: string,
        type: 'assets' | 'liabilities'
    ) => {
        if (!section) {
            return (
                <div className="border rounded p-4 text-center text-muted">
                    No {type} data available for the selected period.
                </div>
            );
        }

        const filteredNodes = filterNodesByQuery(section.items || [], query);
        const orderedNodes = type === 'liabilities' ? reorderNetSummaryNodes(filteredNodes) : filteredNodes;

        return (
            <div className="table-responsive">
                <table className="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th>Ledger</th>
                            <th className="text-end">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredNodes.length === 0 && (
                            <tr>
                                <td colSpan={2} className="text-center text-muted py-3">
                                    No matching records.
                                </td>
                            </tr>
                        )}
                        {renderNodes(orderedNodes, type)}
                        <tr className="fw-bold table-light">
                            <td>Total {section.title}</td>
                            <td className="text-end">
                                <span style={{ borderBottom: '3px double #000' }}>{formatAmount(section.total)}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const getProjectList = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + 'projects', getRequestOptions());
            const data = await response.json();
            const options: ProjectOption[] = (data?.data || []).map((item: any) => ({
                value: String(item.id_projects),
                label: item.project_name,
            }));

            setProjects(options);
            setSelectedProject((prev) => prev ?? options[0] ?? null);
        } catch (error) {
            console.error('Failed to load projects', error);
        }
    }, []);

    const geBalanceSheet = useCallback(async () => {
        if (!startDate || !endDate) {
            return;
        }

        const payload: Record<string, string> = {
            startDate,
            endDate,
        };

        if (selectedProject?.value) {
            payload.idProjects = selectedProject.value;
        }

        showLoader();
        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/balance-sheet-v2',
                postRequestOptions(payload)
            );
            const data = await response.json();

            if (data.success) {
                setReportData(data.data || null);
            } else {
                setReportData(null);
            }
        } catch (error) {
            console.error('Failed to load balance sheet', error);
            setReportData(null);
        } finally {
            setLoading(false);
            Swal.close();
        }
    }, [endDate, selectedProject, startDate]);

    useEffect(() => {
        getProjectList();
    }, [getProjectList]);

    return (
        <div className="card card-custom">
            <div className="card-header">
                <div className="card-title">
                    <span className="text fw-bolder" style={{ marginLeft: '10px' }}>
                        Balance Sheet (GL)
                    </span>
                </div>
                <div className="card-toolbar">
                    <div className="d-flex justify-content-end align-items-center flex-wrap">
                        <Select
                            value={selectedProject}
                            className="form-control-sm h-50px w-250px me-3"
                            onChange={(project) => setSelectedProject(project)}
                            options={projects}
                            placeholder="Select project"
                            isClearable
                        />
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <div className="d-flex align-items-center">
                                <span className="me-2">From:</span>
                                <input
                                    onChange={(e) => setStartDate(e.target.value)}
                                    value={startDate}
                                    type="date"
                                    className="form-control form-control-sm"
                                />
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="me-2">To:</span>
                                <input
                                    onChange={(e) => setEndDate(e.target.value)}
                                    value={endDate}
                                    type="date"
                                    className="form-control form-control-sm"
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => geBalanceSheet()}
                            className="btn btn-sm btn-primary ms-2 p-2 me-2"
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                {reportData?.header && (
                    <div className="mb-4 text-center">
                        <h5 className="fw-bold mb-1">
                            Project: {reportData.header.projectName ?? 'All Projects'}
                        </h5>
                        <div>
                            <strong>Period:</strong> {reportData.header.fromDate} to {reportData.header.toDate}
                        </div>
                    </div>
                )}

                <div className="row">
                    <div className="col-lg-6 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold mb-0">Assets</h6>
                            <input
                                type="text"
                                className="form-control form-control-sm w-50"
                                placeholder="Search assets..."
                                value={searchAssets}
                                onChange={(e) => setSearchAssets(e.target.value)}
                            />
                        </div>
                        {renderSection(reportData?.assets, searchAssets, 'assets')}
                    </div>
                    <div className="col-lg-6 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold mb-0">Liabilities</h6>
                            <input
                                type="text"
                                className="form-control form-control-sm w-50"
                                placeholder="Search liabilities..."
                                value={searchLiabilities}
                                onChange={(e) => setSearchLiabilities(e.target.value)}
                            />
                        </div>
                        {renderSection(reportData?.liabilities, searchLiabilities, 'liabilities')}
                    </div>
                </div>
            </div>
        </div>
    );
}